import { Hero } from '@/components/Hero'
import { laravelGraphqlEndpoint } from '@/globals/globals'
import { UserContext } from '@/lib/UserContext'
import {
  AspectRatio,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Input,
  Link,
  List,
  ListItem,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import { captureEvent } from '@sentry/nextjs'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import request, { gql } from 'graphql-request'
import { useContext, useEffect, useState } from 'react'

export const JuiceBoxPage = () => {
  // Get current user
  const userData = useContext(UserContext)()

  const [hasEntry, setHasEntry] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dateChecked, setDateChecked] = useState(false)
  const toast = useToast()
  const [contestEnded, setContestEnded] = useState(false)

  // Get current time to see if contest has ended
  useEffect(() => {
    fetch('/api/current-time')
      .then(r => r.json())
      .then(r => {
        const year = dayjs(r).year()
        const month = dayjs(r).month()

        if (year > 2024 && month > 0) {
          setContestEnded(true)
        }

        setDateChecked(true)
      })
  }, [])

  // Get contest entries from user
  useEffect(() => {
    const fetchData = async () => {
      let query = gql`
        query ($user: String!) {
          contestEntriesByUser(
            user_uuid: $user
            contest_slug: "gifraffe-tools-giveaway"
          ) {
            id
            user {
              uuid
            }
          }
        }
      `
      let variables = {
        user: userData?.uuid || '',
      }
      await request(laravelGraphqlEndpoint, query, variables)
        .then(r => {
          if (r.contestEntriesByUser.length > 0) {
            setHasEntry(true)
          }
        })
        .finally(() => setIsLoaded(true))
    }

    fetchData()
  }, [userData])

  // Contest entry submission mutation
  const handleSubmit = async (values: any) => {
    if (!userData) {
      toast({
        title: 'You must be logged in to enter the contest.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    setIsSubmitting(true)

    let query = gql`
      mutation ($user: ID!, $challenge_answer: String!) {
        createContestEntry(
          contest_slug: "gifraffe-tools-giveaway"
          user_uuid: $user
          challenge_answer: $challenge_answer
        ) {
          id
          user {
            uuid
          }
        }
      }
    `
    let variables = {
      user: userData.uuid,
      challenge_answer: values.challengeAnswer,
    }
    await request(laravelGraphqlEndpoint, query, variables)
      .then(r => {
        if (r.createContestEntry) {
          setHasEntry(true)
        }
      })
      .catch(error => {
        toast({
          title: 'Error submitting entry',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        captureEvent(error, { data: 'Error submitting contest entry' })
      })
      .finally(() => setIsSubmitting(false))
  }

  const formik = useFormik({
    initialValues: {
      challengeAnswer: '',
    },

    onSubmit: values => {
      handleSubmit(values)
    },
  })

  return (
    <>
      <Hero
        heading='Giraffe Tools Giveaway'
        subheading='Grandfalls Retractable Pressure Washer PRO+ Grandstorm Retractable Vacuum Cleaner'>
        <Box maxW='5xl' mx='auto'>
          <AspectRatio ratio={16 / 9} maxW='100%' maxH='100%' overflow='hidden'>
            <iframe
              src='https://www.youtube-nocookie.com/embed/xGvNmIktmbE'
              title='YouTube video player'
              frameBorder='0'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen'></iframe>
          </AspectRatio>
        </Box>
      </Hero>
      <Container py={20} textAlign='center' maxWidth='6xl'>
        <Stack align='center' gap={8}>
          <Box>
            <Text mb={3}>ONE PERSON HAS A CHANCE TO WIN:</Text>
            <List spacing={2} mb={2}>
              <ListItem fontWeight='bold'>
                1 (#){' '}
                <a
                  href='https://giraffetools.com/products/grandfalls-pressure-washer-pro?variant=41678718238779'
                  target='_blank'
                  rel='noreferrer'>
                  Grandfalls Retractable Pressure Washer PRO
                </a>
              </ListItem>
              <ListItem fontWeight='bold'>
                1 (#){' '}
                <a
                  href='https://giraffetools.com/products/retractable-vacuum-cleaner?variant=41550664368187'
                  target='_blank'
                  rel='noreferrer'>
                  Grandstorm Retractable Vacuum Cleaner
                </a>
              </ListItem>
            </List>
            <Text mb={8}>(Combined Retail Value or “ARV” $1089.98 USD)</Text>
            <Text>
              Annual memberships and merch orders will be rewarded additional
              entries, see{' '}
              <Link
                href='/contest-terms-and-conditions'
                textDecor='underline'
                target='_blank'>
                Terms for details!
              </Link>
            </Text>
          </Box>
          {isLoaded && dateChecked && !userData && !contestEnded && (
            <Stack gap={4}>
              <Text>You must be logged in to enter the contest.</Text>
              <Flex gap={4} justify='center'>
                <Button
                  colorScheme='red'
                  as='a'
                  href='/login?redirectTo=/contest'>
                  Login
                </Button>
                <Button
                  colorScheme='red'
                  as='a'
                  href='/register?redirectTo=/contest'>
                  Signup
                </Button>
              </Flex>
            </Stack>
          )}
          {userData && isLoaded && dateChecked && hasEntry && (
            <p>You have an entry in this contest!</p>
          )}
          {userData && isLoaded && dateChecked && !hasEntry && !contestEnded && (
            <Box p={8} backgroundColor='gray.100'>
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={4}>
                  <Box>
                    <Heading as='h2' size='xl' mb={2}>
                      Enter the giveaway:
                    </Heading>
                    <Text fontWeight='bold' mb={2}>
                      What is the slogan stickered on our white Land Rover
                      Defender?
                    </Text>
                  </Box>
                  <Flex gap={2} justify='center'>
                    <Input
                      required
                      placeholder='Answer'
                      name='challengeAnswer'
                      id='challengeAnswer'
                      type='text'
                      maxW='48'
                      onChange={formik.handleChange}
                      value={formik.values.challengeAnswer}
                    />
                    <Button
                      type='submit'
                      isLoading={isSubmitting}
                      colorScheme='red'>
                      Enter Giveaway
                    </Button>
                  </Flex>
                </Stack>
              </form>
            </Box>
          )}
          {dateChecked && contestEnded ? (
            <Text>Contest has now closed.</Text>
          ) : (
            <Text>Contest runs until January 31st, 2025</Text>
          )}
          <Text>
            <Link
              href='/contest-terms-and-conditions'
              textDecor='underline'
              target='_blank'>
              Contest Terms & Conditions
            </Link>
          </Text>
        </Stack>
      </Container>
    </>
  )
}

export default JuiceBoxPage
